import axios, { AxiosInstance } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import DialerTreeConfig from "@/app/config";
import { authService } from "@/services/authService";

const axiosInstance: AxiosInstance = axios.create();

const REFRESH_TOKEN_ENDPOINT = "/v1/auth/refresh";

const performTokenRefresh = async (): Promise<void> => {
  const refreshToken = await authService.getRefreshToken();

  if (!refreshToken) {
    return Promise.reject(new Error("Cannot find refresh token"));
  }

  try {
    const tokenRefreshResponse = await axios.post(`${DialerTreeConfig.baseAPIEndpoint}${REFRESH_TOKEN_ENDPOINT}`, {
      refreshToken,
    });

    const responseData = tokenRefreshResponse.data?.data || tokenRefreshResponse.data;
    const accessToken = responseData?.accessToken;
    const newRefreshToken = responseData?.refreshToken;

    if (!accessToken) {
      throw new Error("No access token in refresh response");
    }

    await authService.setAccessToken(accessToken);
    if (newRefreshToken) {
      await authService.setRefreshToken(newRefreshToken);
    }

    window.dispatchEvent(new Event("storage"));

    return Promise.resolve();
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 401 || status === 403) {
      await authService.logout();
    }
    return Promise.reject(error);
  }
};

let refreshTokensInFlight: Promise<void> | null = null;

export function refreshTokens(): Promise<void> {
  if (!refreshTokensInFlight) {
    refreshTokensInFlight = performTokenRefresh().finally(() => {
      refreshTokensInFlight = null;
    });
  }
  return refreshTokensInFlight;
}

export function refreshTokensIfExpired(): void {
  if (!authService.isAccessTokenExpired()) {
    return;
  }
  refreshTokens().catch(() => undefined);
}

createAuthRefreshInterceptor(axiosInstance as any, () => refreshTokens(), {
  shouldRefresh(error) {
    const errorStatus = error.response?.status;
    const isRefreshRoute = error.config?.url?.includes(REFRESH_TOKEN_ENDPOINT);
    return errorStatus === 401 && !isRefreshRoute;
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const isLoginRoute = config.url?.includes(`${DialerTreeConfig.baseAPIEndpoint}/v1/auth/login`);
    const isRefreshRoute = config.url?.includes(REFRESH_TOKEN_ENDPOINT);

    if (config.headers && !isLoginRoute && !isRefreshRoute) {
      config.headers["Request-Start-Time"] = Date.now();

      const bearer = authService.getBearerToken();
      if (bearer) {
        config.headers.Authorization = bearer;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  async (response) => {
    const responseData = response.data?.data || response.data;

    if (responseData?.accessToken && responseData?.refreshToken) {
      await authService.setAccessToken(responseData.accessToken);
      await authService.setRefreshToken(responseData.refreshToken);
      window.dispatchEvent(new Event("storage"));
    }

    if (responseData?.IdToken && responseData?.AccessToken && responseData?.RefreshToken) {
      await authService.setIdToken(responseData.IdToken);
      await authService.setAccessToken(responseData.AccessToken);
      await authService.setRefreshToken(responseData.RefreshToken);
      window.dispatchEvent(new Event("storage"));
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export default axiosInstance;
